import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { actualizarPerfilPortal, cerrarSesionPortal, crearConsultaPortal, listarConsultasPortal, obtenerPortalSocio, portalLogin } from '../services/socioPortalService'

const STORAGE_KEY = 'cspah_portal_session'
const SocioPortalContext = createContext(null)

// La sesión del socio vive solo en sessionStorage y es independiente de AuthContext.
function readStoredToken() {
  try { return sessionStorage.getItem(STORAGE_KEY) || '' } catch { return '' }
}

function writeStoredToken(token) {
  try {
    if (token) sessionStorage.setItem(STORAGE_KEY, token)
    else sessionStorage.removeItem(STORAGE_KEY)
  } catch { /* sessionStorage no disponible: la sesión dura lo que la pestaña */ }
}

export function SocioPortalProvider({ children }) {
  const [token, setToken] = useState(readStoredToken)
  const [profile, setProfile] = useState(null)
  // Solo hay carga inicial si existe una sesión guardada que restaurar.
  const [loading, setLoading] = useState(() => Boolean(readStoredToken()))

  const clearSession = useCallback(() => {
    writeStoredToken('')
    setToken('')
    setProfile(null)
    setLoading(false)
  }, [])

  // Restaura la sesión guardada una única vez al montar (F5 en /socio/inicio).
  useEffect(() => {
    const storedToken = readStoredToken()
    if (!storedToken) return undefined
    let active = true
    obtenerPortalSocio(storedToken)
      .then((data) => { if (active) setProfile(data) })
      .catch(() => { if (active) clearSession() })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [clearSession])

  const signIn = useCallback(async (rut, pin) => {
    const session = await portalLogin(rut, pin)
    try {
      const data = await obtenerPortalSocio(session.token)
      writeStoredToken(session.token)
      setToken(session.token)
      setProfile(data)
      return session
    } catch (error) {
      await cerrarSesionPortal(session.token).catch(() => {})
      throw error
    }
  }, [])

  const signOut = useCallback(async () => {
    const currentToken = token
    clearSession()
    if (currentToken) await cerrarSesionPortal(currentToken).catch(() => {})
  }, [token, clearSession])

  // Guarda el perfil; la Edge Function devuelve el perfil completo actualizado.
  const updateProfile = useCallback(async (payload) => {
    try {
      const data = await actualizarPerfilPortal(token, payload)
      setProfile(data)
      return data
    } catch (error) {
      if (error.status === 401) clearSession()
      throw error
    }
  }, [token, clearSession])

  // Las consultas se piden bajo demanda; un 401 cierra la sesión del portal igual que en el perfil.
  const withSession = useCallback(async (call) => {
    try { return await call(token) } catch (error) {
      if (error.status === 401) clearSession()
      throw error
    }
  }, [token, clearSession])
  const listConsultas = useCallback(() => withSession(listarConsultasPortal), [withSession])
  const createConsulta = useCallback((categoria, consulta) => withSession((t) => crearConsultaPortal(t, categoria, consulta)), [withSession])

  return (
    <SocioPortalContext.Provider value={{ token, profile, loading, isAuthenticated: Boolean(token && profile), signIn, signOut, updateProfile, listConsultas, createConsulta }}>
      {children}
    </SocioPortalContext.Provider>
  )
}

export function useSocioPortal() {
  const context = useContext(SocioPortalContext)
  if (!context) throw new Error('useSocioPortal debe utilizarse dentro de SocioPortalProvider.')
  return context
}
